import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatCurrency, formatDateTime, formatNumber } from "../lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaymentRow {
  id: string;
  amount: number;
  status: string;
  paid_at: string | null;
  created_at: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface PaymentsResponse {
  payments: PaymentRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaymentStats {
  stats: Array<{ period: string; count: number; total: number; avg: number }>;
  totals: { count: number; total: number; avg: number };
}

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState("day");
  const [days, setDays] = useState(30);
  const limit = 20;

  const { data: paymentsData, isLoading } = useQuery<PaymentsResponse>({
    queryKey: [
      `/api/admin/payments?page=${page}&limit=${limit}${status ? `&status=${status}` : ""}`,
    ],
  });

  const { data: statsData } = useQuery<PaymentStats>({
    queryKey: [`/api/admin/payments/stats?period=${period}&days=${days}`],
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Платежи</h1>

      {/* Summary Cards */}
      {statsData && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">
              Платежей ({days} дн.)
            </div>
            <div className="text-2xl font-bold">
              {formatNumber(statsData.totals.count)}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">
              Выручка ({days} дн.)
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(statsData.totals.total)}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Средний чек</div>
            <div className="text-2xl font-bold">
              {formatCurrency(statsData.totals.avg)}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все статусы</option>
          <option value="succeeded">Успешные</option>
          <option value="pending">Ожидание</option>
          <option value="canceled">Отменённые</option>
        </select>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="7">7 дней</option>
          <option value="30">30 дней</option>
          <option value="90">90 дней</option>
          <option value="365">Год</option>
        </select>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="day">По дням</option>
          <option value="week">По неделям</option>
          <option value="month">По месяцам</option>
        </select>
      </div>

      {/* Stats Table */}
      {statsData && statsData.stats.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Период
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Кол-во
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Сумма
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Средний
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {statsData.stats.map((s, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-sm">
                      {new Date(s.period).toLocaleDateString("ru-RU")}
                    </td>
                    <td className="px-4 py-2 text-sm">{s.count}</td>
                    <td className="px-4 py-2 text-sm font-mono">
                      {formatCurrency(s.total)}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono text-muted-foreground">
                      {formatCurrency(s.avg)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payments List */}
      <h2 className="text-lg font-semibold">Все платежи</h2>
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Пользователь
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Сумма
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Оплачен
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paymentsData?.payments.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Платежи не найдены
                  </td>
                </tr>
              ) : (
                paymentsData?.payments.map((p) => (
                  <tr key={p.id} className="hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <Link href={`/users/${p.user_id}`}>
                        <span className="text-sm text-primary hover:underline cursor-pointer">
                          {p.email}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">
                      {formatCurrency(Number(p.amount))}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          p.status === "succeeded"
                            ? "bg-green-100 text-green-700"
                            : p.status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {p.paid_at ? formatDateTime(p.paid_at) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {paymentsData && paymentsData.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Стр. {paymentsData.page} из {paymentsData.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(paymentsData.totalPages, p + 1))
              }
              disabled={page >= paymentsData.totalPages}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
